import { Arrays, isDefined, isInstanceOf, Option, panic, UUID } from "@opendaw/lib-std";
import { Address } from "./address";
import { PointerField } from "./pointer";
export class GraphEdges {
    #requiresTarget;
    #requiresPointer;
    #requiresExclusive;
    #incoming;
    #outgoing;
    #affected;
    constructor() {
        this.#requiresTarget = Address.newSet(source => source.address);
        this.#requiresPointer = Address.newSet(vertex => vertex.address);
        this.#requiresExclusive = Address.newSet(vertex => vertex.address);
        this.#incoming = Address.newSet(([address]) => address);
        this.#outgoing = Address.newSet(([source]) => source.address);
        this.#affected = UUID.newSet(uuid => uuid);
    }
    watchVertex(vertex) {
        if (isInstanceOf(vertex, PointerField)) {
            if (!vertex.mandatory) {
                return panic("watchVertex called but has no edge requirement");
            }
            this.#requiresTarget.add(vertex);
        }
        else {
            const { mandatory, exclusive } = vertex.pointerRules;
            if (!mandatory && !exclusive) {
                return panic("watchVertex called but has no edge requirement");
            }
            if (mandatory) {
                this.#requiresPointer.add(vertex);
            }
            if (exclusive) {
                this.#requiresExclusive.add(vertex);
            }
        }
        this.#affected.add(vertex.address.uuid);
    }
    unwatchVerticesOf(...boxes) {
        const map = ({ box: { address: { uuid } } }) => uuid;
        for (const { address: { uuid } } of boxes) {
            this.#removeSameBox(this.#requiresTarget, uuid, map);
            this.#removeSameBox(this.#requiresPointer, uuid, map);
            this.#removeSameBox(this.#requiresExclusive, uuid, map);
        }
        for (const box of boxes) {
            const outgoingLinks = this.outgoingEdgesOf(box);
            if (outgoingLinks.length > 0) {
                return panic(`${box} has outgoing edges: ${outgoingLinks.map(([source, target]) => `[${source.toString()}, ${target.toString()}]`)}`);
            }
            const incomingPointers = this.incomingEdgesOf(box);
            if (incomingPointers.length > 0) {
                return panic(`${box} has incoming edges from: ${incomingPointers.map((source) => source.toString())}`);
            }
        }
    }
    // Removes the watch registrations of a box that never entered the graph (failed construction).
    // Unlike unwatchVerticesOf, this must not assert on remaining edges — other boxes created in the
    // same (doomed) transaction may still point at the failed box until the rollback disconnects them.
    forgetVerticesOf(box) {
        const map = ({ box: { address: { uuid } } }) => uuid;
        const { address: { uuid } } = box;
        this.#removeSameBox(this.#requiresTarget, uuid, map);
        this.#removeSameBox(this.#requiresPointer, uuid, map);
        this.#removeSameBox(this.#requiresExclusive, uuid, map);
    }
    connect(source, target) {
        this.#outgoing.add([source, target]);
        this.#incoming.opt(target).match({
            none: () => this.#incoming.add([target, [source]]),
            some: ([, sources]) => sources.push(source)
        });
        this.#affected.add(source.address.uuid);
        this.#affected.add(target.uuid);
    }
    disconnect(source) {
        const [, target] = this.#outgoing.removeByKey(source.address);
        const [, sources] = this.#incoming.get(target, "incoming by address");
        Arrays.remove(sources, source);
        if (sources.length === 0) {
            this.#incoming.removeByKey(target);
        }
        this.#affected.add(source.address.uuid);
        this.#affected.add(target.uuid);
    }
    isConnected(source, target) {
        return this.#outgoing.opt(source.address).mapOr(([, actualTarget]) => actualTarget.equals(target), false);
    }
    outgoingEdgesOf(box) {
        return this.#collectSameBox(this.#outgoing, box.address.uuid, ([{ box: { address: { uuid } } }]) => uuid);
    }
    incomingEdgesOf(vertex) {
        if (vertex.isBox()) {
            return this.#collectSameBox(this.#incoming, vertex.address.uuid, ([{ uuid }]) => uuid)
                .flatMap(([_, pointers]) => pointers);
        }
        else {
            return this.#incoming.opt(vertex.address).mapOr(([_, pointers]) => pointers, Arrays.empty());
        }
    }
    // Every mandatory pointer in the graph is registered here, so this is the authoritative answer to
    // "who is missing a required target" without walking (possibly nested) fields box by box.
    unsatisfiedMandatoryPointers() {
        return this.#requiresTarget.values().filter(pointer => pointer.isEmpty());
    }
    clearAffected() { this.#affected.clear(); }
    tryValidateAffected() {
        const map = ({ box: { address: { uuid } } }) => uuid;
        for (const uuid of this.#affected.values()) {
            const pointers = this.#collectSameBox(this.#requiresTarget, uuid, map);
            for (const pointer of pointers) {
                if (pointer.isEmpty()) {
                    this.#affected.clear();
                    if (pointer.mandatory) {
                        return Option.wrap(new Error(`Pointer ${pointer.toString()} requires an edge.`));
                    }
                    else {
                        return Option.wrap(new Error(`Illegal state: ${pointer} has no edge requirements.`));
                    }
                }
            }
            const targets = this.#collectSameBox(this.#requiresPointer, uuid, map);
            for (const target of targets) {
                if (target.pointerHub.isEmpty()) {
                    this.#affected.clear();
                    if (target.pointerRules.mandatory) {
                        return Option.wrap(new Error(`Target ${target.toString()} requires an edge.`));
                    }
                    else {
                        return Option.wrap(new Error(`Illegal state: ${target} has no edge requirements.`));
                    }
                }
            }
            const exclusives = this.#collectSameBox(this.#requiresExclusive, uuid, map);
            for (const target of exclusives) {
                const count = target.pointerHub.size();
                if (count > 1) {
                    this.#affected.clear();
                    return Option.wrap(new Error(`Target ${target.toString()} is exclusive but has ${count} incoming pointers.`));
                }
            }
        }
        this.#affected.clear();
        return Option.None;
    }
    validateRequirements() {
        // TODO I removed the assertions because they were too slow in busy graphs.
        //  I tried to use a Set<Box> in BoxGraph, but that wasn't faster than the SortedSet.
        //  We could just use a boolean in Box, but it could be set from the outside world and break it.
        //  Claude suggest to use dirty sets, but I am too lazy to implement it right now.
        // const now = performance.now()
        this.#requiresTarget.forEach(pointer => {
            // assert(pointer.isAttached(), `Pointer ${pointer.address.toString()} is not attached`)
            if (pointer.isEmpty()) {
                if (pointer.mandatory) {
                    console.warn(`[GraphEdges] Validation failed: Pointer ${pointer.toString()} requires an edge.`);
                    return panic(`Pointer ${pointer.toString()} requires an edge.`);
                }
                else {
                    return panic(`Illegal state: ${pointer} has no edge requirements.`);
                }
            }
        });
        this.#requiresPointer.forEach(target => {
            // assert(target.isAttached(), `Target ${target.address.toString()} is not attached`)
            if (target.pointerHub.isEmpty()) {
                if (target.pointerRules.mandatory) {
                    console.warn(target);
                    console.warn(`[GraphEdges] Validation failed: Target ${target.toString()} requires an edge.`);
                    return panic(`Target ${target.toString()} requires an edge.`);
                }
                else {
                    return panic(`Illegal state: ${target} has no edge requirements.`);
                }
            }
        });
        this.#requiresExclusive.forEach(target => {
            const count = target.pointerHub.size();
            if (count > 1) {
                console.warn(`[GraphEdges] Validation failed: Target ${target.toString()} is exclusive but has ${count} incoming pointers.`);
                return panic(`Target ${target.toString()} is exclusive but has ${count} incoming pointers.`);
            }
        });
        // console.debug(`GraphEdges validation took ${performance.now() - now} ms.`)
    }
    #collectSameBox(set, id, map) {
        const range = Address.boxRange(set, id, map);
        return isDefined(range) ? set.values().slice(range[0], range[1]) : Arrays.empty();
    }
    #removeSameBox(set, id, map) {
        const range = Address.boxRange(set, id, map);
        if (isDefined(range)) {
            set.removeRange(range[0], range[1]);
        }
    }
}
